import type { UserResource } from "@exonn/js-sdk";
import type { StorageKey } from "@exonn/storage";
import { setUserDataCache } from "src/user-data/cache/setUserDataCache.ts";
import { getUserDataState } from "src/user-data/getUserDataState.ts";
import { useUserDataApi } from "src/user-data/useUserDataApi.ts";
import { deepEq } from "@exonn/utils";
import { useMemoizedFn } from "@exonn/hooks";
import type { ZodType } from "zod";

export function useUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V;
};
export function useUserDataState<
  V = any,
  D extends V | undefined = V | undefined,
>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V | undefined;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V | undefined;
};
export function useUserDataState<
  V = any,
  D extends V | undefined = V | undefined,
>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V | undefined;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V | undefined;
} {
  const {
    key,
    user,
    defaultValue,
    sanitaze,
    disableSavingToDatabase,
    validationSchema,
  } = options;
  const { setUserData } = useUserDataApi();
  const value = getUserDataState<V>({
    key,
    user,
    validationSchema,
    defaultValue,
    sanitaze,
  });

  const getValue = useMemoizedFn(() => {
    return getUserDataState<V>({
      user,
      key,
      defaultValue,
      validationSchema,
      sanitaze,
    });
  });

  const setValue = useMemoizedFn((value: V | null) => {
    const prevState = getValue();
    if (!deepEq(prevState, value)) {
      setUserDataCache<V>({
        user,
        key,
        value,
      });
      if (!disableSavingToDatabase) {
        setUserData<V>({
          key: key,
          value: value,
        });
      }
    }
  });

  const clear = useMemoizedFn(() => {
    setValue(null);
  });

  return {
    value,
    setValue,
    getValue,
    clear,
  };
}
